import { useMemo, useState, type MouseEvent } from 'react';
import { likelySamePerson, unlinkLeavesNoSignIn } from '@technohull/shared';
import {
  useAdminGoogleLink,
  useAdminGooglePrimary,
  useAdminGoogleUnlink,
  useAdminUsers,
  useRemoveAlias,
  useCreateUser,
  useDeleteUser,
  useSetActive,
  useSetPassword,
  useSetUserName,
  useSetUserOverrides,
  useSetUserRoles,
  type AdminUser,
  type UsersResponse,
} from '../../features/admin.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ErrorState, FilterBar, FilterSelect, InlineError, LoadingState, Modal, PageHeader, SearchInput, SyncLabel, useConfirm } from '../../components/ui.js';
import { IconTrash } from '../../components/icons.js';
import { GoogleAccountLine, GoogleMark, googleAccountHint } from '../../components/GoogleAccount.js';
import { useDateFormatter } from '../../lib/format.js';
import { useIsCoarse, useIsPhone } from '../../lib/media.js';
import { fold, hasText } from '../../lib/search.js';
import { AdminBack } from './AdminHub.js';
import { DuplicateNotice, MergeDialog } from './MergeUsers.js';

type ActiveFilter = 'all' | 'active' | 'inactive';
type OvValue = 'inherit' | 'grant' | 'revoke';

// the card's own controls (Active, Manage, Delete) must not also open the card
const stop = (e: MouseEvent) => e.stopPropagation();

export function AdminUsers() {
  const df = useDateFormatter(); // last login in the viewer's own time and date format
  const { data, isLoading, isFetching, error, refetch } = useAdminUsers();
  const { me } = useAuth();
  const isPhone = useIsPhone();
  const setActive = useSetActive();
  const createUser = useCreateUser();
  const delUser = useDeleteUser();
  const [confirmNode, confirm] = useConfirm();
  const [manage, setManage] = useState<string | null>(null);
  const [merging, setMerging] = useState<{ source: string; target: string } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState('');
  const [roleF, setRoleF] = useState('');
  const [activeF, setActiveF] = useState<ActiveFilter>('all');
  const activeFilters = (q.trim() ? 1 : 0) + (roleF ? 1 : 0) + (activeF !== 'all' ? 1 : 0);
  const clearFilters = () => { setQ(''); setRoleF(''); setActiveF('all'); };

  const onDelete = async (u: AdminUser) => {
    if (u.email === me?.user.email) return; // can't delete yourself
    const ok = await confirm({
      title: 'Delete user',
      message:
        `Delete ${u.display_name || u.email} (${u.email})? This permanently removes their account, roles, ` +
        `permission overrides and departments, and logs them out. It cannot be undone. History of who did ` +
        `what is kept.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) delUser.mutate(u.email);
  };

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('sales');
  const [password, setPassword] = useState('');

  const create = () => {
    createUser.mutate({ email, displayName: name, role, password }, { onSuccess: () => { setEmail(''); setName(''); setPassword(''); setShowAdd(false); } });
  };

  const managed = data?.users.find((u) => u.email === manage) ?? null;
  const roleLabelOf = (name: string) => data?.roles.find((x) => x.name === name)?.label ?? name;
  // a person's roles always read in one order, the roles list's (as in Manage), not the order they were given
  const roleRank = useMemo(() => new Map((data?.roles ?? []).map((r, i) => [r.name, i])), [data]);
  const rolesOf = (u: AdminUser): string[] =>
    [...(u.roles.length ? u.roles : [u.role])].sort((a, b) => (roleRank.get(a) ?? Infinity) - (roleRank.get(b) ?? Infinity) || a.localeCompare(b));

  const rows = useMemo(() => {
    const users = data?.users ?? [];
    const query = fold(q.trim());
    return users.filter((u) => {
      if (activeF === 'active' && !u.active) return false;
      if (activeF === 'inactive' && u.active) return false;
      const userRoles = u.roles.length ? u.roles : [u.role];
      if (roleF && !userRoles.includes(roleF)) return false;
      if (query) {
        const hay = [u.email, u.display_name, ...userRoles.map(roleLabelOf)].join(' ');
        if (!hasText(query, hay)) return false;
      }
      return true;
    });
  }, [data, q, roleF, activeF]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = data?.users.length ?? 0;
  const count = rows.length === total ? `${total} user${total === 1 ? '' : 's'}` : `${rows.length} of ${total}`;

  return (
    <>
      <AdminBack />
      <PageHeader title="Users">
        {data && <span className="muted page-count">{count}</span>}
        <button className="btn btn-sm btn-primary" onClick={() => setShowAdd((s) => !s)}>{showAdd ? 'Close' : '+ Add user'}</button>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} idle="Refresh" busyText="Refreshing…" /></button>
      </PageHeader>

      {showAdd && (
        <div className="page-panel">
          <h2 className="adm-panel-title" style={{ fontSize: 14, marginBottom: 10 }}>Add user</h2>
          <div className="adm-form">
            <label>
              <span className="field-label">Email</span>
              <input className="field" type="email" inputMode="email" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                placeholder="name@technohull.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              <span className="field-label">Display name</span>
              <input className="field" placeholder="First and last name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              <span className="field-label">Role</span>
              <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
                {(data?.roles ?? []).map((r) => (<option key={r.name} value={r.name}>{r.label}</option>))}
              </select>
            </label>
            <label>
              <span className="field-label">Password</span>
              <input className="field" type="password" autoComplete="new-password" placeholder="At least 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <button className="btn btn-primary adm-form-submit" disabled={createUser.isPending || !email || password.length < 8} onClick={create}>{createUser.isPending ? 'Creating…' : 'Create user'}</button>
          </div>
          {createUser.isError && <InlineError error={createUser.error} onDismiss={() => createUser.reset()} />}
        </div>
      )}

      {data && <DuplicateNotice users={data.users} onMerge={(source, target) => setMerging({ source, target })} />}

      <FilterBar
        search={<SearchInput value={q} onChange={setQ} placeholder="Search name / email / role" width={220} />}
        activeCount={activeFilters}
        onClear={clearFilters}
      >
        <FilterSelect
          label="Role"
          ariaLabel="Filter by role"
          value={roleF}
          onChange={setRoleF}
          options={[{ value: '', label: 'All roles' }, ...(data?.roles ?? []).map((r) => ({ value: r.name, label: r.label }))]}
        />
        <FilterSelect
          label="Status"
          ariaLabel="Filter by status"
          value={activeF}
          onChange={(v) => setActiveF(v as ActiveFilter)}
          options={[{ value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Suspended' }]}
        />
      </FilterBar>

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {delUser.isError && <InlineError error={delUser.error} onDismiss={() => delUser.reset()} />}
      {setActive.isError && <InlineError error={setActive.error} onDismiss={() => setActive.reset()} />}
      {data && (
        <div className="tbl-wrap">
          {/* phones: a card per person: the name (or the address) with Manage, the role pills, "n perms ·
              last login", then the Active switch and Delete. Tapping the card opens Manage too. */}
          <table className="tbl tbl-cards adm-users">
            <thead>
              <tr><th>User</th><th>Roles</th><th>Perms</th><th>Active</th><th>Last login</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                // dim only the identity cells: dimming the row made the live controls look disabled
                const dim = u.active ? '' : ' is-suspended';
                const login = u.last_seen_at ? `last login ${df.dateTime(u.last_seen_at, { year: 'auto' })}` : 'never signed in';
                return (
                  <tr key={u.email} className={isPhone ? 'tc-tap' : undefined} onClick={isPhone ? () => setManage(u.email) : undefined}>
                    <td className={`tc-title u-id${dim}`}>
                      {/* no display name: the address's local part heads the card instead of a bare "-" */}
                      <div className="u-name">{u.display_name || u.email.split('@')[0]}</div>
                      <div className="muted u-email">{u.email}</div>
                      {u.google && (
                        <div className="muted u-google" title={`Google account${u.google.primary ? ' (primary: their emails go here)' : ''}`}>
                          <GoogleMark size={11} /> {u.google.email}{u.google.primary ? ' · primary' : ''}
                        </div>
                      )}
                      {(u.aliases ?? []).map((a) => (
                        <div key={a.email} className="muted u-alias" title="Merged into this account: signing in with it opens this account">also {a.email}</div>
                      ))}
                    </td>
                    <td className={`u-roles${dim}`}>
                      <div className="u-pills">
                        {rolesOf(u).map((r) => (
                          <span key={r} className="pill accent">{roleLabelOf(r)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="muted tc-hide">{u.effective.length}</td>
                    <td className="u-active" onClick={stop}>
                      <label className={isPhone ? 'check-row' : undefined}>
                        <input type="checkbox" aria-label={`${u.display_name || u.email} active`} checked={!!u.active} disabled={setActive.isPending} onChange={(e) => setActive.mutate({ email: u.email, active: e.target.checked })} />
                        {isPhone && <span>Active</span>}
                      </label>
                    </td>
                    <td className="muted tc-hide" style={{ whiteSpace: 'nowrap' }}>{u.last_seen_at ? df.dateTime(u.last_seen_at) : '-'}</td>
                    <td className="tc-meta u-meta">{u.effective.length} perms · {login}</td>
                    <td className="u-act" onClick={stop}>
                      <div className="u-act-row">
                        <button className="btn btn-sm u-manage" onClick={() => setManage(u.email)}>Manage</button>
                        {u.email !== me?.user.email && (
                          <button className="btn btn-sm btn-ghost btn-del u-delete" disabled={delUser.isPending} onClick={() => onDelete(u)} title="Delete this user">
                            <IconTrash size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>No users match those filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {managed && data && (
        <ManageUserModal
          user={managed}
          data={data}
          onClose={() => setManage(null)}
          onMerge={() => {
            // the likely other account (if any) to keep: open the merge with this one going into it
            const other = data.users.find((x) => x.email !== managed.email && likelySamePerson(
              { email: managed.email, displayName: managed.display_name }, { email: x.email, displayName: x.display_name }));
            setManage(null);
            setMerging({ source: managed.email, target: other?.email ?? '' });
          }}
        />
      )}
      {merging && data && (
        <MergeDialog users={data.users} me={me?.user.email} initial={merging} rolesText={(u) => rolesOf(u).map(roleLabelOf).join(', ')} roleLabel={roleLabelOf} onClose={() => setMerging(null)} />
      )}
      {confirmNode}
    </>
  );
}

function ManageUserModal({ user, data, onClose, onMerge }: { user: AdminUser; data: UsersResponse; onClose: () => void; onMerge: () => void }) {
  const setUserRoles = useSetUserRoles();
  const setOverrides = useSetUserOverrides();
  const setPassword = useSetPassword();
  const setName = useSetUserName();
  const coarse = useIsCoarse();
  const [confirmNode, confirm] = useConfirm();
  // bumped to re-mount the dialog (and re-arm its back gesture) when a back-gesture close is cancelled
  const [armKey, setArmKey] = useState(0);

  const [name, setName2] = useState(user.display_name);
  const [pw, setPw] = useState('');
  const savedRoles = user.roles.length ? user.roles : [user.role];
  const [roles, setRoles] = useState<string[]>(savedRoles);
  const savedOv = useMemo(() => {
    const m: Record<string, OvValue> = {};
    for (const o of user.overrides) m[o.permission] = o.granted ? 'grant' : 'revoke';
    return m;
  }, [user.overrides]);
  const [ov, setOv] = useState<Record<string, OvValue>>(() => ({ ...savedOv }));

  // unsaved edits: the role ticks and the override selects (name and password save on their own buttons)
  const rolesDirty = roles.length !== savedRoles.length || roles.some((r) => !savedRoles.includes(r));
  const ovDirty = useMemo(
    () => [...new Set([...Object.keys(ov), ...Object.keys(savedOv)])].some((k) => (ov[k] ?? 'inherit') !== (savedOv[k] ?? 'inherit')),
    [ov, savedOv],
  );
  const dirty = rolesDirty || ovDirty;
  const saving = setUserRoles.isPending || setOverrides.isPending;

  // a fresh edit clears the previous "Saved" label.
  const toggleRole = (r: string) => {
    if (setUserRoles.isSuccess) setUserRoles.reset();
    setRoles((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
  };

  const overridesPayload = () =>
    Object.entries(ov).filter(([, v]) => v !== 'inherit').map(([permission, v]) => ({ permission, granted: v === 'grant' }));
  const saveRoles = () => setUserRoles.mutate({ email: user.email, roles });
  const saveOverrides = () => setOverrides.mutate({ email: user.email, overrides: overridesPayload() });

  // footer "Save changes": whichever section is dirty, then close. A failure stays open with its section's error.
  const saveAll = async () => {
    try {
      await Promise.all([
        rolesDirty ? setUserRoles.mutateAsync({ email: user.email, roles }) : null,
        ovDirty ? setOverrides.mutateAsync({ email: user.email, overrides: overridesPayload() }) : null,
      ]);
      onClose();
    } catch { /* shown inline under the section that failed */ }
  };

  // Escape, the scrim, the x and the back gesture all land here: unsaved role/override edits ask first
  const requestClose = async () => {
    if (!dirty) { onClose(); return; }
    // the back gesture has already spent this dialog's history entry by now
    const viaBack = coarse && (window.history.state as { __overlay?: number } | null)?.__overlay == null;
    const discard = await confirm({
      title: 'Discard changes?',
      message: `${[rolesDirty && 'Roles', ovDirty && 'permission overrides'].filter(Boolean).join(' and ')} for ${user.display_name || user.email} have unsaved changes.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true,
    });
    if (discard) onClose();
    else if (viaBack) setArmKey((k) => k + 1);
  };

  return (
    <>
      <Modal
        key={armKey}
        title={user.display_name || user.email}
        width={640}
        className="adm-manage"
        onClose={() => void requestClose()}
        footer={dirty ? (
          <>
            <button className="btn btn-ghost" disabled={saving} onClick={onClose}>Discard</button>
            <button className="btn btn-primary" disabled={saving} onClick={() => void saveAll()}>{saving ? 'Saving…' : 'Save changes'}</button>
          </>
        ) : (
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        )}
      >
        <div className="cfg-cat-hd">Display name</div>
        <div style={{ display: 'flex', gap: 8, margin: '6px 0 4px' }}>
          <input className="field" aria-label="Display name" style={{ flex: 1 }} value={name} maxLength={120} onChange={(e) => setName2(e.target.value)} />
          <button className="btn btn-sm btn-primary" disabled={setName.isPending || !name.trim() || name.trim() === user.display_name} onClick={() => setName.mutate({ email: user.email, displayName: name.trim() })}>
            {setName.isPending ? 'Saving…' : 'Save name'}
          </button>
        </div>
        {setName.isError && <InlineError error={setName.error} onDismiss={() => setName.reset()} />}
        <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>{user.email}</div>

        {/* inline instead of window.prompt: the new password stays masked and the result is shown */}
        <div className="cfg-cat-hd">Reset password</div>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, margin: '6px 0 4px' }}>
            <input
              className="field"
              type="password"
              autoComplete="new-password"
              aria-label="New password"
              placeholder="New password (min 8)"
              style={{ flex: 1 }}
              value={pw}
              onChange={(e) => { setPw(e.target.value); if (!setPassword.isIdle) setPassword.reset(); }}
            />
            <button
              className="btn btn-sm btn-primary"
              disabled={setPassword.isPending || pw.length < 8}
              onClick={() => setPassword.mutate({ email: user.email, password: pw }, { onSuccess: () => setPw('') })}
            >
              {setPassword.isPending ? 'Saving…' : 'Set password'}
            </button>
          </div>
          {pw && pw.length < 8 && <div className="muted">At least 8 characters.</div>}
          {setPassword.isSuccess && <div className="muted" role="status">Password updated.</div>}
          {setPassword.isError && <InlineError error={setPassword.error} onDismiss={() => setPassword.reset()} />}
        </div>

        <div className="cfg-cat-hd">Google account</div>
        <GoogleSection user={user} signInReady={!!data.googleSignIn} confirm={confirm} />

        <div className="cfg-cat-hd">Same person, another account</div>
        <AliasSection user={user} confirm={confirm} onMerge={onMerge} />

        <div className="cfg-cat-hd">Roles</div>
        <div className="perm-grid" style={{ marginBottom: 6 }}>
          {data.roles.map((r) => (
            <label key={r.name} className="perm-switch">
              <input type="checkbox" checked={roles.includes(r.name)} onChange={() => toggleRole(r.name)} />
              <span>{r.label}</span>
            </label>
          ))}
        </div>
        <button className="btn btn-sm btn-primary" disabled={setUserRoles.isPending} onClick={saveRoles}>
          {setUserRoles.isPending ? 'Saving…' : setUserRoles.isSuccess ? 'Saved' : 'Save roles'}
        </button>
        {setUserRoles.isError && <InlineError error={setUserRoles.error} onDismiss={() => setUserRoles.reset()} />}

        <div className="cfg-cat-hd" style={{ marginTop: 14 }}>Permission overrides</div>
        <p className="muted" style={{ margin: '2px 0 8px' }}>
          Page access follows the user's combined role permissions plus these overrides - anything
          granted here (or via a role) is immediately visible to them. Inherit uses the role
          permissions; Grant/Revoke forces an exception for this user.
        </p>
        {data.modules.map((mod) => {
          const perms = data.catalog.filter((c) => c.module === mod);
          if (!perms.length) return null;
          return (
            <div key={mod} className="perm-module">
              <div className="perm-module-hd">{mod}</div>
              {perms.map((p) => {
                const inherited = user.effective.includes(p.id);
                const val = ov[p.id] ?? 'inherit';
                return (
                  <div key={p.id} className="ov-row">
                    <span>{p.label} {inherited && val === 'inherit' && <span className="pill ok" style={{ marginLeft: 4 }}>via role</span>}</span>
                    <select
                      className="field"
                      aria-label={`${p.label} override`}
                      style={{ width: 110 }}
                      value={val}
                      onChange={(e) => {
                        if (setOverrides.isSuccess) setOverrides.reset();
                        setOv((s) => ({ ...s, [p.id]: e.target.value as OvValue }));
                      }}
                    >
                      <option value="inherit">Inherit</option>
                      <option value="grant">Grant</option>
                      <option value="revoke">Revoke</option>
                    </select>
                  </div>
                );
              })}
            </div>
          );
        })}
        <button className="btn btn-sm btn-primary" disabled={setOverrides.isPending} onClick={saveOverrides}>
          {setOverrides.isPending ? 'Saving…' : setOverrides.isSuccess ? 'Saved' : 'Save overrides'}
        </button>
        {setOverrides.isError && <InlineError error={setOverrides.error} onDismiss={() => setOverrides.reset()} />}
      </Modal>
      {/* outside the dialog: a sheet's transform would trap the confirm's fixed layer inside it */}
      {confirmNode}
    </>
  );
}

// connect someone's Google account by its address (it's confirmed the first time that Google account signs
// in), make it primary so their Rolodex emails go there, or disconnect it.
function GoogleSection({ user, signInReady, confirm }: {
  user: AdminUser;
  signInReady: boolean;
  confirm: ReturnType<typeof useConfirm>[1];
}) {
  const df = useDateFormatter();
  const link = useAdminGoogleLink();
  const primary = useAdminGooglePrimary();
  const unlink = useAdminGoogleUnlink();
  const [addr, setAddr] = useState('');
  const acct = user.google;
  const name = user.display_name || user.email;
  const busy = link.isPending || primary.isPending || unlink.isPending;
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr.trim());

  const onDisconnect = async () => {
    if (!acct) return;
    const stranded = unlinkLeavesNoSignIn(user.email, acct.email, !!user.has_password);
    const ok = await confirm({
      title: 'Disconnect Google account',
      message:
        `Disconnect ${acct.email} from ${name}? ` +
        (stranded
          ? `It's how they sign in and they have no password, so they won't be able to sign in until you set one.`
          : `Signing in with Google as ${acct.email} will no longer open this account.`) +
        (acct.primary ? ` Their Rolodex emails go back to ${user.email}.` : ''),
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (ok) unlink.mutate(user.email);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      {acct ? (
        <>
          <GoogleAccountLine account={acct} />
          <p className="muted" style={{ margin: '4px 0 8px' }}>
            {googleAccountHint(acct, user.email, name)}
            {acct.lastSignInAt ? ` Last Google sign-in ${df.dateTime(acct.lastSignInAt, { year: 'auto' })}.` : ''}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {acct.primary ? (
              <button className="btn btn-sm" disabled={busy} onClick={() => primary.mutate({ email: user.email, primary: false })}>Make Rolodex address primary</button>
            ) : (
              <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => primary.mutate({ email: user.email, primary: true })}>Make primary</button>
            )}
            <button className="btn btn-sm btn-ghost btn-del" disabled={busy} onClick={() => void onDisconnect()}>Disconnect</button>
          </div>
        </>
      ) : (
        <p className="muted" style={{ margin: '4px 0 6px' }}>
          Not connected. Connect their Google account so signing in with Google opens this account
          {signInReady ? '' : ' (once Google sign-in is set up)'}, and make it primary to send their Rolodex emails there.
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, margin: '8px 0 4px' }}>
        <input
          className="field"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Google account address"
          placeholder={acct ? 'A different Google address' : 'Their Google address'}
          style={{ flex: 1 }}
          value={addr}
          onChange={(e) => { setAddr(e.target.value); if (!link.isIdle) link.reset(); }}
        />
        <button
          className="btn btn-sm btn-primary"
          disabled={busy || !valid}
          onClick={() => link.mutate({ email: user.email, google: addr.trim() }, { onSuccess: () => setAddr('') })}
        >
          {link.isPending ? 'Connecting…' : acct ? 'Change' : 'Connect'}
        </button>
      </div>
      {link.isSuccess && (
        <div className="muted" role="status">
          Connected.{link.data?.removed ? ` The separate, never-approved account ${link.data.removed} was removed.` : ''}
        </div>
      )}
      {link.isError && <InlineError error={link.error} onDismiss={() => link.reset()} />}
      {primary.isError && <InlineError error={primary.error} onDismiss={() => primary.reset()} />}
      {unlink.isError && <InlineError error={unlink.error} onDismiss={() => unlink.reset()} />}
    </div>
  );
}

// addresses merged into this account (they sign in to it), and the way to merge another account in
function AliasSection({ user, confirm, onMerge }: { user: AdminUser; confirm: ReturnType<typeof useConfirm>[1]; onMerge: () => void }) {
  const remove = useRemoveAlias();
  const aliases = user.aliases ?? [];
  const df = useDateFormatter();
  const name = user.display_name || user.email;
  const onRemove = async (alias: string) => {
    const ok = await confirm({
      title: 'Stop this address signing in',
      message: `${alias} will no longer open ${name}'s account. Signing in with it again would start a new account waiting for approval.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (ok) remove.mutate(alias);
  };
  return (
    <div style={{ marginBottom: 8 }}>
      {aliases.length > 0 ? (
        <ul className="u-aliases">
          {aliases.map((a) => (
            <li key={a.email}>
              <span><b>{a.email}</b> <span className="muted">· merged {df.date(a.mergedAt, { year: 'auto' })}, signs in to this account</span></span>
              <button className="btn btn-sm btn-ghost btn-del" disabled={remove.isPending} onClick={() => void onRemove(a.email)}>Remove</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted" style={{ margin: '4px 0 6px' }}>If {name} also has an account under another address, merge the two so they're one person here.</p>
      )}
      <button className="btn btn-sm" onClick={onMerge}>Merge with another account…</button>
      {remove.isError && <InlineError error={remove.error} onDismiss={() => remove.reset()} />}
    </div>
  );
}
